// ════════════════════════════════════════════════════════════
//  Fighter.jsx — THE ADAPTER: the kit owns the body, we own the pose
// ════════════════════════════════════════════════════════════
//  `ieBeingModels.jsx` is CROSS-GAME CANONICAL and is never edited
//  here — not one line, not one colour. Everything that makes a
//  being FIGHT lives in this file, wrapped around the body from the
//  outside: the hop of a beast's run, the lean into a lunge, the
//  squash on impact, the spin when it's struck, the flicker of an
//  Intangible veil, the sink of a ruin, the rise of an Ascent.
//
//  This file poses the body's ROOT. The LIMBS (run, guard, strikes,
//  casts, flinches, the head on the foe) are LimbRig.jsx's, through
//  the kit's rig names (2026-10-20). The hand-off between the two is
//  the shared S ref: this frame (always first) publishes the being,
//  its stride and its dt there and calls `S.pre()`, which puts every
//  limb back clean BEFORE the body's own frame runs — so the kit's
//  idle and our pose never pile up (the pose law: set, never pile up).
//  The kit's own idle life (tails, ears, wisps) keeps running.
//
//  Movement never re-renders React. The ONLY setState here is the
//  rare moment the body itself changes (a swap or an Ascent).
// ════════════════════════════════════════════════════════════
import { useRef, useState, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { IEBeing } from "../ieBeingModels.jsx";
import LimbRig from "./LimbRig.jsx";
import { glowTex } from "./fxTex.js";
import { LINES, KIN_MOTION } from "../data/roster.js";
import { colorOf, TRANSCEND_KHAO, transcendWait } from "../sim/battle.js";
import { TIER_ORDER } from "../data/roster.js";

// How tall each tier should stand on the field. Bodies are measured
// once when they mount and wrapped in ONE scale group to reach this
// — the kit's internal numbers are never touched. Dials.
export const STAND_HEIGHT = { beb: 1.75, champion: 2.15, lord: 2.65 };
const SCALE_CLAMP = [0.75, 1.7];
const ADD = THREE.AdditiveBlending;

export default function Fighter({ battle, side, fx }) {
  // ⚠ LOAD-BEARING: `body` starts null, so this component's useFrame
  // subscribes BEFORE the body's and LimbRig's (they mount later, on the
  // first setBody). R3F runs frames in subscription order — that is what
  // lets S.pre() clean the limbs before the body's own frame. Never mount
  // a Fighter with a body already in it.
  const [body, setBody] = useState(null);   // { uid, line, tier }
  const root = useRef(), pose = useRef(), fit = useRef(), inner = useRef();
  const shadow = useRef(), aura = useRef(), flash = useRef(), veil = useRef();
  const orbA = useRef(), orbB = useRef();
  const glow = useMemo(() => glowTex(), []);
  const S = useRef({ phase: 0, enterT: 0, ruinT: 0, prevState: "", trail: 0, lastX: 0, lastZ: 0, zT: 0, hidden: true, fitScale: 1, spinYaw: 0 });

  // measure the body the frame it mounts, then fit it
  useLayoutEffect(() => {
    if (!body || !inner.current || !fit.current) return;
    fit.current.scale.setScalar(1);
    inner.current.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(inner.current);
    const h = Math.max(0.4, box.max.y - Math.max(box.min.y, -0.3));
    const want = STAND_HEIGHT[body.tier] || 1.6;
    const s = THREE.MathUtils.clamp(want / h, SCALE_CLAMP[0], SCALE_CLAMP[1]);
    S.current.fitScale = s;
    fit.current.scale.setScalar(s);
  }, [body]);

  useFrame(({ clock }, dtRaw) => {
    const st = S.current;
    st.f = null;                       // LimbRig only poses a frame this one published
    const b = battle.current; if (!b) return;
    const sd = b.sides[side];
    // the field shows the active being — or, for a moment after a
    // ruin, the one that just fell
    let f = sd.active >= 0 ? sd.roster[sd.active] : null;
    if (!f && sd._lastRuined != null) f = sd.roster.find((r) => r.uid === sd._lastRuined);
    if (!f || f.state === "bench") {
      if (root.current) root.current.visible = false;
      return;
    }
    if (!body || body.uid !== f.uid || body.tier !== f.tier || body.line !== f.line) {
      // a different body: the one React render this fight costs
      setBody({ uid: f.uid, line: f.line, tier: f.tier });
      st.enterT = f.state === "enter" ? 0 : 1; st.ruinT = 0;
      return;
    }
    const dt = Math.min(dtRaw, 0.05) * (fx.timeScale === 0 ? 0 : f.state === "transcend" ? 1 : b.timeScale);
    const t = clock.elapsedTime;
    const g = root.current, p = pose.current;
    if (!g || !p) return;
    g.visible = true;
    const kin = LINES[f.line].kin;
    const motion = KIN_MOTION[kin];
    const col = colorOf(f);
    // publish for LimbRig, and clean the limbs before the body's frame
    st.f = f; st.dt = dt; st.t = t;
    if (st.pre) st.pre();

    g.position.set(f.x, 0, f.z);
    g.rotation.y = f.yaw;

    const speed = Math.hypot(f.vx, f.vz);
    const sn = Math.min(1, speed / motion.speed);
    st.sn = sn;
    st.phase += dt * (4 + speed * 1.6);

    // ── the gait ──────────────────────────────────────────────
    let y = 0, rx = 0, rz = 0, sx = 1, sy = 1, sz = 1, extraYaw = 0;
    const hover = f.line === "xulan" || (f.line === "h01" && f.tier === "lord");
    if (hover) y = 0.18 + Math.sin(t * 2.1 + side) * 0.08;
    if (kin === "Beast") { y += Math.abs(Math.sin(st.phase)) * 0.22 * sn; rx += 0.12 * sn; }
    else if (kin === "Dragon") { y += Math.abs(Math.sin(st.phase * 0.8)) * 0.12 * sn; rx += 0.1 * sn; }
    else if (kin === "Machine") { rz += Math.sin(st.phase * 0.9) * 0.05 * sn; rx += 0.06 * sn; }
    else { y += Math.abs(Math.sin(st.phase)) * 0.09 * sn; rx += 0.08 * sn; }
    // lean into the turn: roll by sideways velocity
    const fwdX = Math.sin(f.yaw), fwdZ = Math.cos(f.yaw);
    const lateral = (f.vx * fwdZ - f.vz * fwdX) / motion.speed;
    rz += THREE.MathUtils.clamp(-lateral * 0.22, -0.3, 0.3);
    // breathing when still
    sy *= 1 + Math.sin(t * 2.4 + side * 2) * 0.015 * (1 - sn);

    // ── the fight ─────────────────────────────────────────────
    const A = f.anim;
    switch (f.state) {
      case "windup": {
        const k = A.glow;
        rx -= 0.28 * k;               // draw back
        sy *= 1 - 0.08 * k; sx *= 1 + 0.05 * k; sz *= 1 + 0.05 * k;
        if (f.move && f.move.ult) y += 0.35 * k;   // U'hlti: lifted by its own power
        break;
      }
      case "active":
        if (f.move && f.move.kind === "lunge") { rx += 0.45; sz *= 1.14; sy *= 0.9; }
        else if (f.move && (f.move.kind === "beam" || f.move.kind === "bolt")) { rx -= 0.12; }
        else if (f.move && f.move.kind === "nova") { sy *= 1.12; sx *= 0.94; sz *= 0.94; y += 0.1; }
        break;
      case "recover": rx += 0.08; break;
      case "dodge": rx += 0.3; y += 0.12; sy *= 0.9; sz *= 1.1; break;
      case "leap": {
        // the Dragon's Primera: one second through the air, an arc and a tuck
        const k = f.leap ? f.leap.t : 0;
        y += Math.sin(k * Math.PI) * 2.4;
        rx += Math.sin(k * Math.PI * 2) * 0.5;
        sy *= 1 - Math.sin(k * Math.PI) * 0.12; sz *= 1 + Math.sin(k * Math.PI) * 0.1;
        break;
      }
      case "stagger": rx -= 0.35 * Math.min(1, f.stateT * 4 + 0.3); y += 0.05; break;
      case "asleep": rz += 0.45; rx -= 0.1; sy *= 1 + Math.sin(t * 1.6) * 0.04; break;
      case "transcend": {
        // lifted, spun, and swelling — the Ascent
        const k = 1 - Math.max(0, f.stateT) / 2.6;
        y += Math.sin(Math.min(1, k * 1.4) * Math.PI * 0.5) * 1.3;
        st.spinYaw += dt * (2 + k * 18);
        extraYaw = st.spinYaw;
        const pulse = 1 + Math.sin(t * 24) * 0.04 * k;
        sx *= pulse; sy *= pulse; sz *= pulse;
        if (fx.burst && dt > 0 && Math.random() < 0.7) fx.burst(f.x, 0.2, f.z, col, 2, 1.5, { rise: 4, life: 1.1, size: 0.5, spread: 1.6, drag: 1 });
        break;
      }
      default: break;
    }
    // squash from impacts and lunges, spin from being struck
    sy *= 1 - A.squash * 0.12; sz *= 1 + A.squash * 0.1;
    extraYaw += A.spin * Math.sin(t * 38) * 0.35;
    rx -= A.flash * 0.15;

    // ── entrance & ruin ───────────────────────────────────────
    let life = 1;
    if (st.enterT < 1) {
      st.enterT = Math.min(1, st.enterT + dt / 0.55);
      const e = st.enterT;
      life = e < 1 ? 1 - Math.pow(1 - e, 3) * 1.05 + Math.sin(e * Math.PI) * 0.18 : 1;   // a little overshoot
      y += (1 - e) * 2.5;
      extraYaw += (1 - e) * 4;
    }
    if (f.state === "ruined") {
      st.ruinT = Math.min(1, st.ruinT + dt / 1.3);
      const k = st.ruinT;
      rz += k * 1.2; y -= k * 0.4;
      life *= 1 - k;
      if (fx.burst && dt > 0 && k < 0.95 && Math.random() < 0.8) fx.burst(f.x, 0.6, f.z, col, 2, 1, { rise: 3.5, life: 1.6, size: 0.45, spread: 1.2, drag: 0.6 });
    } else st.ruinT = 0;

    st.life = life;
    p.position.y = y;
    p.rotation.set(rx, extraYaw, rz);
    p.scale.set(sx * life, sy * life, sz * life);

    // ── Intangible: the body flickers out of phase ────────────
    const phased = !!(f.statuses.intangible || f.statuses.iframe);
    if (inner.current) inner.current.visible = !phased || Math.floor(t * 14) % 3 !== 0;
    if (veil.current) {
      veil.current.visible = phased;
      veil.current.material.opacity = 0.35 + Math.sin(t * 20) * 0.1;
    }

    // ── glow, flash, shadow ───────────────────────────────────
    const h = (STAND_HEIGHT[f.tier] || 1.6);
    if (aura.current) {
      const lordAura = f.tier === "lord" ? 0.22 + Math.sin(t * 1.7) * 0.05 : f.tier === "champion" ? 0.08 : 0;
      const charge = f.state === "windup" ? A.glow * (f.move && f.move.ult ? 1 : 0.6) : 0;
      const tr = f.state === "transcend" ? 0.9 : 0;
      const o = Math.max(lordAura, charge, tr, f.statuses.empowered ? 0.3 : 0);
      aura.current.material.opacity = o;
      const ac = f.statuses.empowered && o === 0.3 ? "#ffb04a" : col;
      if (st.auraCol !== ac) { aura.current.material.color.set(ac); st.auraCol = ac; }
      aura.current.position.y = y + h * 0.5;
      const s = h * (1.8 + o * 0.6) * life;
      aura.current.scale.set(s, s * 1.15, 1);
    }
    if (flash.current) {
      flash.current.material.opacity = A.flash * 0.9;
      flash.current.position.y = y + h * 0.5;
      flash.current.scale.setScalar(h * 1.5 * life);
    }
    if (shadow.current) {
      const fading = f.state === "ruined";
      const s = (1.2 + h * 0.35) * (1 - Math.min(0.5, y * 0.3)) * (fading ? Math.max(0.01, life) : Math.max(0.2, life));
      shadow.current.scale.set(s, s, 1);
      shadow.current.material.opacity = 0.55 * (fading ? life : Math.max(0.2, life));
    }
    // Soul full: two motes circle it — "it is ready to become"
    // two motes circle a being its Aenarchist can afford to Transcend
    const nextTier = TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1];
    const ready = !!nextTier && transcendWait(b, nextTier) <= 0 && sd.khao >= TRANSCEND_KHAO[nextTier] && f.state !== "transcend" && !f.ruined;
    for (let oi = 0; oi < 2; oi++) {
      const o = oi === 0 ? orbA.current : orbB.current, ph = oi * Math.PI;
      if (!o) continue;
      o.visible = ready;
      if (ready) {
        const a = t * 3 + ph;
        o.position.set(Math.cos(a) * h * 0.55, y + h * (0.45 + Math.sin(t * 2 + ph) * 0.25), Math.sin(a) * h * 0.55);
        if (st.orbCol !== col) { orbA.current.material.color.set(col); orbB.current.material.color.set(col); st.orbCol = col; }
      }
    }

    // ── what the ground remembers: footprints ─────────────────
    if (!hover && f.state !== "transcend" && fx.scar) {
      st.trail += Math.hypot(f.x - st.lastX, f.z - st.lastZ);
      if (st.trail > (kin === "Beast" ? 0.85 : 1.1)) {
        st.trail = 0;
        if (speed > motion.speed * 0.6) fx.scar("smudge", f.x, f.z, { r: 0.28 * (h / 1.6), yaw: f.yaw });
        if (speed > motion.speed * 0.8 && fx.burst) fx.burst(f.x, 0.08, f.z, "#6a5a88", 2, 1.2, { life: 0.5, size: 0.35, flat: true, rise: 0.3 });
      }
    }
    st.lastX = f.x; st.lastZ = f.z;
    // sleeping: the little z's
    if (f.state === "asleep" && fx.text) {
      st.zT -= dt;
      if (st.zT <= 0) { st.zT = 0.9; fx.text(f.x + 0.4, h + 0.4, f.z, "z", "#9ab8ff", { small: true, life: 1.4 }); }
    }
  });

  return (
    <group ref={root} visible={false}>
      <mesh ref={shadow} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} renderOrder={1}>
        <circleGeometry args={[0.5, 24]} />
        <meshBasicMaterial color="#05020a" transparent opacity={0.55} depthWrite={false} />
      </mesh>
      <sprite ref={aura}>
        <spriteMaterial map={glow} transparent opacity={0} depthWrite={false} blending={ADD} toneMapped={false} />
      </sprite>
      <group ref={pose}>
        <group ref={fit}>
          <group ref={inner}>
            {body && <IEBeing key={body.line + body.tier} being={body.line} tier={body.tier} />}
            {/* after the body, so its frame runs after the body's own */}
            {body && <LimbRig key={"rig" + body.line + body.tier} host={inner} battle={battle} side={side} share={S} />}
          </group>
        </group>
        <mesh ref={veil} visible={false} position={[0, 0.9, 0]} scale={[0.9, 1.15, 0.9]}>
          <sphereGeometry args={[1, 18, 12]} />
          <meshBasicMaterial color="#B388FF" transparent opacity={0.35} depthWrite={false} blending={ADD} side={THREE.BackSide} toneMapped={false} />
        </mesh>
      </group>
      <sprite ref={flash}>
        <spriteMaterial map={glow} transparent opacity={0} depthWrite={false} blending={ADD} toneMapped={false} color="#ffffff" />
      </sprite>
      <sprite ref={orbA} visible={false} scale={[0.35, 0.35, 1]}>
        <spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} />
      </sprite>
      <sprite ref={orbB} visible={false} scale={[0.35, 0.35, 1]}>
        <spriteMaterial map={glow} transparent depthWrite={false} blending={ADD} toneMapped={false} />
      </sprite>
    </group>
  );
}
